import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Zone } from './hooks/useOrgZones';
import useTemplateMutations from './hooks/useTemplateMutations';

interface ZoneEditorProps {
  zones: Zone[];
  onRefresh: () => void;
  isPaivcamSeed?: boolean;
}

interface ZoneFormData {
  name: string;
  description: string;
}

const EMPTY_FORM: ZoneFormData = { name: '', description: '' };

function generateCode(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

// ── Sortable Zone Row ──

interface SortableZoneRowProps {
  zone: Zone;
  onEdit: (zone: Zone) => void;
  onDelete: (zone: Zone) => void;
  disabled: boolean;
}

function SortableZoneRow({
  zone,
  onEdit,
  onDelete,
  disabled,
}: SortableZoneRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: zone.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${theme.custom?.stroke}`,
        backgroundColor: isDragging
          ? theme.custom?.isDark
            ? 'var(--fi-accent-soft, rgba(255, 106, 0, 0.15))'
            : 'var(--fi-accent-soft, #FFF3E0)'
          : 'transparent',
        '&:last-child': { borderBottom: 'none' },
        '&:hover': {
          backgroundColor: theme.custom?.background2,
        },
      })}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon
          sx={(theme) => ({
            fontSize: 20,
            color: theme.custom?.textLight,
          })}
        />
      </Box>

      <Box
        flex={1}
        sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        onClick={() => onEdit(zone)}
      >
        <Typography
          sx={(theme) => ({
            fontSize: 14,
            fontWeight: 500,
            color: theme.custom?.text,
          })}
        >
          {zone.name}
        </Typography>
        {zone.description && (
          <Typography
            sx={(theme) => ({
              fontSize: 12,
              color: theme.custom?.textLight,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {zone.description}
          </Typography>
        )}
      </Box>

      <IconButton
        size="small"
        onClick={() => onDelete(zone)}
        disabled={disabled}
        color="error"
        aria-label="Delete zone"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

// ── Main Component ──

function ZoneEditor({
  zones,
  onRefresh,
  isPaivcamSeed = false,
}: ZoneEditorProps) {
  const { createZone, updateZone, deleteZone } = useTemplateMutations();

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newZone, setNewZone] = useState<ZoneFormData>(EMPTY_FORM);
  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [editForm, setEditForm] = useState<ZoneFormData>(EMPTY_FORM);
  const [mutating, setMutating] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const systemZones = useMemo(
    () =>
      [...zones.filter((z) => z.isSystem)].sort(
        (a, b) => a.sortOrder - b.sortOrder
      ),
    [zones]
  );

  const customZones = useMemo(
    () =>
      [...zones.filter((z) => !z.isSystem)].sort(
        (a, b) => a.sortOrder - b.sortOrder
      ),
    [zones]
  );

  const nextSortOrder = useMemo(() => {
    const allOrders = zones.map((z) => z.sortOrder);
    return allOrders.length > 0 ? Math.max(...allOrders) + 100 : 100;
  }, [zones]);

  // ── Drag-and-drop reorder ──
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = customZones.findIndex((z) => z.id === active.id);
      const newIndex = customZones.findIndex((z) => z.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove([...customZones], oldIndex, newIndex);

      setMutating(true);
      try {
        const updates = reordered
          .map((z, i) => ({ id: z.id, newSortOrder: (i + 1) * 100 }))
          .filter((u) => {
            const original = customZones.find((z) => z.id === u.id);
            return original && original.sortOrder !== u.newSortOrder;
          });

        for (const u of updates) {
          await updateZone(u.id, { sortOrder: u.newSortOrder });
        }
        onRefresh();
      } catch (err: any) {
        showSnackbar(err?.message ?? 'Failed to reorder zones', 'error');
      } finally {
        setMutating(false);
      }
    },
    [customZones, updateZone, onRefresh]
  );

  // ── Create ──
  const handleCreate = async () => {
    if (!newZone.name.trim()) return;
    setMutating(true);
    try {
      const result = await createZone({
        name: newZone.name.trim(),
        code: generateCode(newZone.name),
        description: newZone.description.trim() || undefined,
        sortOrder: nextSortOrder,
      });
      if (result?.success) {
        showSnackbar('Zone created', 'success');
        setNewZone(EMPTY_FORM);
        setCreateDialogOpen(false);
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to create zone', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to create zone', 'error');
    } finally {
      setMutating(false);
    }
  };

  // ── Edit ──
  const handleStartEdit = (zone: Zone) => {
    setEditingZone(zone);
    setEditForm({ name: zone.name, description: zone.description ?? '' });
  };

  const handleSaveEdit = async () => {
    if (!editingZone || !editForm.name.trim()) return;
    setMutating(true);
    try {
      const result = await updateZone(editingZone.id, {
        name: editForm.name.trim(),
        description: editForm.description.trim() || undefined,
      });
      if (result?.success) {
        showSnackbar('Zone updated', 'success');
        setEditingZone(null);
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to update zone', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to update zone', 'error');
    } finally {
      setMutating(false);
    }
  };

  // ── Delete ──
  const handleDelete = async (zone: Zone) => {
    setMutating(true);
    try {
      const result = await deleteZone(zone.id);
      if (result?.success) {
        showSnackbar('Zone deleted', 'success');
        onRefresh();
      } else {
        showSnackbar(result?.error ?? 'Failed to delete zone', 'error');
      }
    } catch (err: any) {
      showSnackbar(err?.message ?? 'Failed to delete zone', 'error');
    } finally {
      setMutating(false);
    }
  };

  return (
    <Box>
      {/* Description + New Zone button */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Typography
          variant="body2"
          sx={(theme) => ({ flex: 1, mr: 2, color: theme.custom?.textLight })}
        >
          Zones organize checklist tasks and damage documentation by location —
          like &quot;Ground&quot; and &quot;Roof&quot;. Inspectors work through
          tasks zone by zone during an inspection.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => {
            setNewZone(EMPTY_FORM);
            setCreateDialogOpen(true);
          }}
          disabled={mutating}
          sx={{ flexShrink: 0 }}
        >
          New Zone
        </Button>
      </Box>

      {/* System zones — only visible to paivcam-seed */}
      {isPaivcamSeed && systemZones.length > 0 && (
        <Box mb={3}>
          <Typography
            variant="subtitle2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            gutterBottom
          >
            System Zones
          </Typography>
          <Paper
            variant="outlined"
            sx={(theme) => ({
              borderRadius: '10px',
              overflow: 'hidden',
              borderColor: theme.custom?.stroke,
              backgroundColor: theme.custom?.background1,
            })}
          >
            {systemZones.map((zone) => (
              <Box
                key={zone.id}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${theme.custom?.stroke}`,
                  '&:last-child': { borderBottom: 'none' },
                })}
              >
                <LockIcon
                  sx={(theme) => ({
                    fontSize: 18,
                    color: theme.custom?.textLight,
                  })}
                />
                <Box flex={1}>
                  <Typography
                    sx={(theme) => ({
                      fontSize: 14,
                      fontWeight: 500,
                      color: theme.custom?.text,
                    })}
                  >
                    {zone.name}
                  </Typography>
                  {zone.description && (
                    <Typography
                      sx={(theme) => ({
                        fontSize: 12,
                        color: theme.custom?.textLight,
                      })}
                    >
                      {zone.description}
                    </Typography>
                  )}
                </Box>
                <Chip
                  label="System"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Box>
            ))}
          </Paper>
        </Box>
      )}

      {/* Org zones — drag-and-drop */}
      <Box>
        {isPaivcamSeed && (
          <Typography
            variant="subtitle2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
            gutterBottom
          >
            Org Zones
          </Typography>
        )}
        {customZones.length === 0 ? (
          <Typography
            variant="body2"
            sx={(theme) => ({ color: theme.custom?.textLight })}
          >
            No custom zones yet. Click &quot;New Zone&quot; to create one.
          </Typography>
        ) : (
          <Paper
            variant="outlined"
            sx={(theme) => ({
              borderRadius: '10px',
              overflow: 'hidden',
              borderColor: theme.custom?.stroke,
              backgroundColor: theme.custom?.background1,
            })}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={customZones.map((z) => z.id)}
                strategy={verticalListSortingStrategy}
              >
                {customZones.map((zone) => (
                  <SortableZoneRow
                    key={zone.id}
                    zone={zone}
                    onEdit={handleStartEdit}
                    onDelete={handleDelete}
                    disabled={mutating}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Paper>
        )}
      </Box>

      {/* Create Zone Dialog — only Name + Description */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Zone</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={newZone.name}
              onChange={(e) =>
                setNewZone((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={newZone.description}
              onChange={(e) =>
                setNewZone((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newZone.name.trim() || mutating}
          >
            {mutating ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Zone Dialog — only Name + Description */}
      <Dialog
        open={!!editingZone}
        onClose={() => setEditingZone(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Zone</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={editForm.name}
              onChange={(e) =>
                setEditForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Description (optional)"
              value={editForm.description}
              onChange={(e) =>
                setEditForm((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingZone(null)}>Cancel</Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editForm.name.trim() || mutating}
          >
            {mutating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default ZoneEditor;
